<template>
  <div style="height: fit-content">
    <div
      v-if="showTopBar"
      style="
        background: rgb(242, 242, 242);
        height: 36px;
        border: 1px solid rgb(191, 191, 191);
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        display: flex;
        align-items: center;
        padding: 0 12px;
        gap: 8px;
      "
    >
      <svg fill="none" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" fill="#F22929" r="6" />
      </svg>
      <svg fill="none" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" fill="#E5B91A" r="6" />
      </svg>
      <svg fill="none" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" fill="#00B929" r="6" />
      </svg>
    </div>
    <div
      v-if="loading"
      class="tw:border-default-500 tw:bg-white tw:border tw:border-t-0 tw:flex tw:justify-center tw:items-center tw:h-80"
    >
      <EdroneLoading is-loading />
    </div>
    <div
      v-if="parsedHtml"
      :class="{ 'tw:border-default-500 tw:border tw:border-t-0': showTopBar }"
      :style="`height: ${iframeHeight}; ${loading ? 'display: none;' : ''}`"
    >
      <PreviewIframe :html="parsedHtml.split(edroneFooterStart)?.[0]" @load="setIframeHeight" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, onUnmounted, computed } from "vue";
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import { getExampleProducts } from "vue-app/api/ProductsService.ts";
import { BasicProductData } from "vue-app/components/common/EmailPreview/EmailPreview.vue";
import PreviewIframe from "vue-app/components/PreviewIframe/PreviewIframe.vue";

interface Props {
  showTopBar?: boolean;
  html?: string | null;
  seed?: number;
  forceLoading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showTopBar: true,
  html: "",
  seed: undefined,
  forceLoading: false,
});

const edroneFooterStart = '<div id="unlayer-edrone-frame"';
// we remove the footer from the html, because it is not needed in the preview. Div starts with this id and have some styles on it so we split on what it starts with

const iframeHeight = ref("100%");

const products = ref<BasicProductData[]>([]);
const iframeLoading = ref(true);
const productsLoading = ref(true);

const parsedHtml = computed(() => {
  if (props.forceLoading) {
    return "";
  }
  return parseHtml(props.html, products.value);
});

const loading = computed(() => props.forceLoading || productsLoading.value || iframeLoading.value);

async function init() {
  products.value = await getExampleProducts(props.seed);
  productsLoading.value = false;
}

function setIframeHeight(document: Document) {
  const height = document.body.scrollHeight;
  iframeHeight.value = Math.round(height) + 1 + "px";
  // unlayer content is in a table with id="u_body", so we need to observe its height for correct height calculation
  const u_body = document.querySelector("#u_body") as HTMLElement;
  if (u_body) {
    resizeObserver.observe(u_body);
    iframeLoading.value = false;
  } else {
    setTimeout(() => {
      const height = document.body.scrollHeight;
      iframeHeight.value = Math.round(height) + 1 + "px";
      iframeLoading.value = false;
    }, 100);
  }
}

const resizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    iframeHeight.value = Math.round(entry.contentRect.height) + 1 + "px";
  });
});

onMounted(async () => {
  await init();
});

onUnmounted(() => {
  resizeObserver.disconnect();
});

function getProducts(products: BasicProductData[]) {
  const finalProductList: BasicProductData[] = [];
  if (products) {
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      if (p && p.title && p.price && p.image) {
        finalProductList.push(p);
      }
    }
  }
  return finalProductList;
}

function parseHtml(html: string | null, products: BasicProductData[]) {
  if (!html) {
    return "";
  }
  let currencySign = "$";
  if (window.EDRONE_CONTEXT.user.currency === "EUR") {
    currencySign = "€";
  } else if (window.EDRONE_CONTEXT.user.currency === "GBP") {
    currencySign = "£";
  } else if (window.EDRONE_CONTEXT.user.currency === "PLN") {
    currencySign = "zł";
  } else if (window.EDRONE_CONTEXT.user.currency === "BRL") {
    currencySign = "R$";
  }

  const finalProductList = getProducts(products);

  let parsedHtml = html;
  for (let i = 1; i <= 6; i++) {
    ["DYNAMIC_ITEM_", "BESTSELLER_ITEM_", "NEWEST_ITEM_", "RECOMMENDATED_ITEM_"].forEach((name) => {
      if (!parsedHtml.includes("*|" + name + i)) {
        return;
      }

      let price: string = "79,99";
      const product = finalProductList[i - 1];
      if (product && product.price) {
        price = new Intl.NumberFormat().format(product.price);
      }
      let oldPrice: string = "89,99";
      if (product && product.oldPrice) {
        oldPrice = new Intl.NumberFormat().format(product.oldPrice);
      }
      let title: string = "Example title";
      if (product && product.title) {
        title = product.title;
      }
      let image: string = "https://control.edrone.me/assets/newsletter-builder/_row_thumbs/1x-square.jpg";
      if (product && product.image) {
        image = product.image;
      }

      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_IMAGE_URL|*", image);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_TITLE|*", title);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_PRICE|*", price);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_OLD_PRICE|*", oldPrice);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_CURRENCY|*", currencySign);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_OLD_CURRENCY|*", currencySign);
    });
  }
  while (parsedHtml.includes("*|IMAGE_WIDTH|*")) {
    parsedHtml = parsedHtml.replace("*|IMAGE_WIDTH|*", "500");
  }
  const fontSize = parsedHtml.match(/data-title-font-size="(\d+)"/);
  const height = fontSize ? Number(fontSize[1]) * 3 : 48;
  while (parsedHtml.includes("*|TITLE_HEIGHT|*")) {
    parsedHtml = parsedHtml.replace("*|TITLE_HEIGHT|*", `${height}`);
  }
  while (parsedHtml.includes("*|TRACKER_ID|*")) {
    parsedHtml = parsedHtml.replace("*|TRACKER_ID|*", String(window.EDRONE_CONTEXT.trackerId));
  }
  while (parsedHtml.includes("*|CUSTOMER_EMAIL|*")) {
    parsedHtml = parsedHtml.replace("*|CUSTOMER_EMAIL|*", String(window.EDRONE_CONTEXT.user.email));
  }
  while (parsedHtml.includes("*|FRAME_UNSUBSCRIBE_TEXT|*")) {
    parsedHtml = parsedHtml.replace("*|FRAME_UNSUBSCRIBE_TEXT|*", "Unsubscribe");
  }
  parsedHtml = parsedHtml.replace(".u-row .u-col img {", ".notUsedInPreview {");
  parsedHtml = parsedHtml.replace(
    "</style>",
    "@media only screen and (max-width: 500px) { .product-title { height: auto !important } } .product-title p {overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; /* number of lines to show */ line-clamp: 2; -webkit-box-orient: vertical;} .u-row-footer--edrone .u-col-50 { width: 50% !important; max-width: 50% !important; min-width: 50% !important;}</style>"
  );
  return parsedHtml;
}
</script>
